
import { defineComponent, computed, PropType } from 'vue'

// Composables
import { UseDateRange } from '@/use/date-range'
import { UseOrder } from '@/use/order'
import { Group, ColumnInfo } from '@/tracing/use-explore-spans'

// Components
import GroupsTableRow from '@/tracing/GroupsTableRow.vue'
import PagedSpansCardLazy from '@/tracing/PagedSpansCardLazy.vue'

// Misc
import { isSpanSystem, AttrKey } from '@/models/otel'
import { updateColumnMap, emptyMetricColumn, MetricColumn } from '@/metrics/types'

export default defineComponent({
  name: 'GroupsTable',
  components: {
    GroupsTableRow,
    PagedSpansCardLazy,
  },

  props: {
    dateRange: {
      type: Object as PropType<UseDateRange>,
      required: true,
    },
    systems: {
      type: Array as PropType<string[]>,
      required: true,
    },
    query: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      required: true,
    },
    groups: {
      type: Array as PropType<Group[]>,
      required: true,
    },
    columns: {
      type: Array as PropType<ColumnInfo[]>,
      required: true,
    },
    plottableColumns: {
      type: Array as PropType<ColumnInfo[]>,
      required: true,
    },
    plottedColumns: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    order: {
      type: Object as PropType<UseOrder>,
      required: true,
    },
    dense: {
      type: Boolean,
      default: false,
    },
    showSystem: {
      type: Boolean,
      default: false,
    },
    hideActions: {
      type: Boolean,
      default: false,
    },
    axiosParams: {
      type: Object,
      required: true,
    },
  },

  setup(props) {
    const hasGroupName = computed((): boolean => {
      return hasColumn(AttrKey.displayName)
    })

    const hasSystemColumn = computed(() => {
      if (props.columns.findIndex((col) => col.name === AttrKey.spanSystem) >= 0) {
        return true
      }
      if (!props.showSystem) {
        return false
      }
      return hasColumn(AttrKey.spanSystem)
    })

    const groupingColumns = computed(() => {
      return props.columns.filter((col) => col.isGroup).map((col) => col.name)
    })

    const plainColumns = computed(() => {
      const blacklist: string[] = [AttrKey.spanSystem as string]
      if (hasGroupName.value) {
        blacklist.push(AttrKey.spanGroupId, AttrKey.displayName)
      }
      return props.columns.filter((col) => {
        if (props.plottableColumns.findIndex((item) => item.name === col.name) >= 0) {
          return false
        }
        if (blacklist.indexOf(col.name) >= 0) {
          return false
        }
        return true
      })
    })

    const headers = computed(() => {
      const headers = []

      if (hasGroupName.value) {
        headers.push({
          text: 'Group name',
          value: '_name',
          sortable: true,
          align: 'start',
        })
      }

      for (let col of plainColumns.value) {
        headers.push({
          text: shortColumnName(col.name),
          value: col.name,
          sortable: true,
          align: 'start',
        })
      }

      if (hasSystemColumn.value) {
        headers.push({
          text: 'System',
          value: AttrKey.spanSystem,
          sortable: true,
          align: 'start',
        })
      }

      for (let col of props.plottableColumns) {
        headers.push({
          text: shortColumnName(col.name),
          value: col.name,
          sortable: true,
          align: 'start',
        })
      }

      headers.push({ text: '', value: 'actions', sortable: false, align: 'end' })

      return headers
    })

    const columnMap = computed((): Record<string, MetricColumn> => {
      const colMap: Record<string, MetricColumn> = {}
      updateColumnMap(colMap, props.columns, emptyMetricColumn)
      return colMap
    })

    function hasColumn(name: string): boolean {
      if (props.groups.length) {
        const item = props.groups[0]
        return name in item
      }

      const index = props.columns.findIndex((col) => col.name === name)
      return index >= 0
    }

    function shortColumnName(name: string) {
      switch (name) {
        case AttrKey.spanCountPerMin:
          return 'count'
        case AttrKey.spanErrorCount:
          return 'errors'
        case AttrKey.spanErrorRate:
          return 'err%'
      }

      let m = name.match(/^([0-9a-z]+)\(_duration\)$/)
      if (m) {
        return m[1]
      }

      m = name.match(/^(\w+)_per_(\w+)$/)
      if (m) {
        return `${m[1]}/${m[2]}`
      }

      return name
    }

    function isSpanSystemGroup(group: Group) {
      const system = group[AttrKey.spanSystem]
      if (system) {
        return isSpanSystem(system)
      }
      return isSpanSystem(...props.systems)
    }

    return {
      AttrKey,

      groupingColumns,
      plainColumns,
      headers,
      columnMap,

      isSpanSystemGroup,
    }
  },
})
